package com.tovtalk.ui.theme

import androidx.compose.animation.core.AnimationSpec
import androidx.compose.animation.core.CubicBezierEasing
import androidx.compose.animation.core.FastOutLinearInEasing
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearOutSlowInEasing
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf

object TovTalkDurations {
    const val instant = 0
    const val fastest = 100
    const val fast = 150
    const val normal = 200
    const val medium = 300
    const val slow = 400
    const val slower = 500
    const val slowest = 600
    const val ripple = 150
    const val fadeIn = 200
    const val fadeOut = 150
    const val slideIn = 300
    const val slideOut = 250
    const val scale = 200
    const val color = 150
    const val expand = 300
    const val collapse = 250
    const val pageTransition = 350
    const val likeAnimation = 250
    const val shimmer = 1500
}

object TovTalkEasings {
    val standard = FastOutSlowInEasing
    val decelerate = LinearOutSlowInEasing
    val accelerate = FastOutLinearInEasing
    val smooth = CubicBezierEasing(0.4f, 0.0f, 0.2f, 1.0f)
    val bouncy = CubicBezierEasing(0.34f, 1.56f, 0.64f, 1.0f)
    val snappy = CubicBezierEasing(0.0f, 0.0f, 0.2f, 1.0f)
    val gentle = CubicBezierEasing(0.4f, 0.0f, 0.6f, 1.0f)
    val enter = CubicBezierEasing(0.0f, 0.0f, 0.2f, 1.0f)
    val exit = CubicBezierEasing(0.4f, 0.0f, 1.0f, 1.0f)
}

@Immutable
data class TovTalkAnimationSpecs(
    val instantTween: AnimationSpec<Float>,
    val fastTween: AnimationSpec<Float>,
    val normalTween: AnimationSpec<Float>,
    val slowTween: AnimationSpec<Float>,
    val likeTween: AnimationSpec<Float>,
    val sheetTween: AnimationSpec<Float>,
    val gentleSpring: AnimationSpec<Float>,
    val responsiveSpring: AnimationSpec<Float>,
    val bouncySpring: AnimationSpec<Float>,
    val stiffSpring: AnimationSpec<Float>,
)

val TovTalkAnimations = TovTalkAnimationSpecs(
    instantTween = tween(durationMillis = TovTalkDurations.instant),
    fastTween = tween(durationMillis = TovTalkDurations.fast, easing = TovTalkEasings.standard),
    normalTween = tween(durationMillis = TovTalkDurations.normal, easing = TovTalkEasings.standard),
    slowTween = tween(durationMillis = TovTalkDurations.slow, easing = TovTalkEasings.standard),
    likeTween = tween(durationMillis = TovTalkDurations.likeAnimation, easing = TovTalkEasings.decelerate),
    sheetTween = tween(durationMillis = TovTalkDurations.slideIn, easing = TovTalkEasings.decelerate),
    gentleSpring = spring(
        dampingRatio = Spring.DampingRatioMediumBouncy,
        stiffness = Spring.StiffnessLow
    ),
    responsiveSpring = spring(
        dampingRatio = Spring.DampingRatioNoBouncy,
        stiffness = Spring.StiffnessMedium
    ),
    bouncySpring = spring(
        dampingRatio = Spring.DampingRatioLowBouncy,
        stiffness = Spring.StiffnessMediumLow
    ),
    stiffSpring = spring(
        dampingRatio = Spring.DampingRatioNoBouncy,
        stiffness = Spring.StiffnessHigh
    ),
)

val LocalTovTalkAnimations = staticCompositionLocalOf { TovTalkAnimations }
